import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Grid, IconButton } from '@mui/material';
import { ControlledField, SelectInputField, UserRole } from 'remote/global';
import { DeleteOutlined, EmailOutlined } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { captureException } from '@sentry/react';
import { usePostOperatorsInvitationMutation } from 'api/queries/operators';
import { AccountInvitationSchemaType, accountInvitationSchema } from './types';
import { createRoleSelectOptions } from '../helpers';

const emptyUserObj = {
  email: '',
  role: '' as unknown as UserRole,
};

const MAX_FIELDS = 5;
function AccountInvitationForm() {
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<AccountInvitationSchemaType>({
    defaultValues: {
      users: [emptyUserObj],
    },
    resolver: zodResolver(accountInvitationSchema),
  });
  const postOperatorsInvitationMutation = usePostOperatorsInvitationMutation();

  const { fields, append, remove } = useFieldArray({
    name: 'users',
    control,
  });

  const onValid = async (data: AccountInvitationSchemaType) => {
    try {
      const processedUsers = await Promise.all(
        // TODO: We should also add "Language" selector to determine the language of the invitee
        data.users.map(async ({ email, role }) =>
          postOperatorsInvitationMutation.mutateAsync({
            email,
            role,
            lang: i18n.language,
          })
        )
      );
      if (processedUsers.every((status) => status === 200)) {
        toast.success(t('Toast.success'));
        // Reset the form
        reset();
      } else {
        toast.error(t('Toast.failure'));
      }
    } catch (err) {
      captureException(err);
    }
  };

  const roleOptions = createRoleSelectOptions(t);

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Grid container spacing={4}>
        <Grid container item xs={12}>
          {fields.map((field, index) => {
            return (
              <Grid container item xs={12} columnSpacing={2} key={field.id} alignItems='center'>
                <Grid item xs={6}>
                  <ControlledField
                    control={control}
                    name={`users.${index}.email`}
                    translationKey='email'
                    fieldProps={{
                      InputProps: {
                        type: 'email',
                        autoComplete: 'email',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <ControlledField
                    control={control}
                    name={`users.${index}.role`}
                    render={(renderProps) => (
                      <SelectInputField
                        {...renderProps}
                        translationKey='role'
                        options={roleOptions}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={2} mt='8px' visibility={index === 0 ? 'hidden' : 'visible'}>
                  <IconButton type='button' onClick={() => remove(index)}>
                    <DeleteOutlined />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
        <Grid container item xs={10}>
          <Grid item xs={12} sm={6}>
            {fields.length <= MAX_FIELDS && (
              <Button
                variant='text'
                onClick={() => {
                  append(emptyUserObj);
                }}
              >
                {t('UsersView.invite.addUser')}
              </Button>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              sx={{
                display: 'flex',
                ml: 'auto',
              }}
              startIcon={<EmailOutlined />}
              type='submit'
              variant='contained'
              color='primary'
              disableElevation
              disabled={isSubmitting || isValidating || isLoading}
            >
              {t('UsersView.invite.send')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
}

export default AccountInvitationForm;
